import { useCallback, useEffect, useState } from 'react';
import {
  operatorConsole,
  type ExportShippingsParams,
  type ListShippingsParams,
  type PoolShippingsResponse,
} from '../../lib/api/operatorConsole';
import { Button } from '../../components/ui/Button';
import { ShippingsFilters } from './ShippingsFilters';
import { EMPTY_FILTERS, type ShippingFilterValues } from './shippingFilters';
import { ShippingRow } from './ShippingRow';

const PER_PAGE = 25;

// Typing a tracking number or an email should not fire a request per keystroke.
const FILTER_DEBOUNCE_MS = 350;

const COLUMNS = [
  'Id',
  'Player',
  'Status updated',
  'Status',
  'Direction',
  'Source',
  'Shipping type',
  'Address',
  'ZIP',
  'Total',
  'Sales tax',
  'Locked FMV',
  'Cards',
];

// Text fields travel trimmed; an id that is not a number is dropped rather
// than sent for the backend to reject.
function filterParams(filters: ShippingFilterValues): ExportShippingsParams {
  const userId = Number(filters.user_id.trim());
  const shippingId = Number(filters.id.trim());

  return {
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.id.trim() && Number.isFinite(shippingId) ? { id: shippingId } : {}),
    ...(filters.certificate_number.trim()
      ? { certificate_number: filters.certificate_number.trim() }
      : {}),
    ...(filters.user_id.trim() && Number.isFinite(userId) ? { user_id: userId } : {}),
    ...(filters.user_email.trim() ? { user_email: filters.user_email.trim() } : {}),
    ...(filters.tracking_number.trim() ? { tracking_number: filters.tracking_number.trim() } : {}),
    ...(filters.created_from ? { created_from: filters.created_from } : {}),
    ...(filters.created_to ? { created_to: filters.created_to } : {}),
  };
}

function toParams(filters: ShippingFilterValues, page: number): ListShippingsParams {
  return { page, per: PER_PAGE, ...filterParams(filters) };
}

// The local calendar day, the same one the date filters are typed in.
function localDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function exportFilename(filters: ShippingFilterValues): string {
  const from = filters.created_from || 'start';
  const to = filters.created_to || localDate(new Date());
  return `deliveries_${from}_${to}.csv`;
}

// The export arrives as a fetched Blob (auth travels in headers), so the
// browser is handed it through a temporary object URL rather than a link.
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoking in the same tick can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function ShippingsPage() {
  const [data, setData] = useState<PoolShippingsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<ShippingFilterValues>(EMPTY_FILTERS);
  // What the last request actually filtered on — the inputs run ahead of it.
  const [appliedFilters, setAppliedFilters] = useState<ShippingFilterValues>(EMPTY_FILTERS);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const fetchShippings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setData(await operatorConsole.listShippings(toParams(appliedFilters, page)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page]);

  useEffect(() => {
    void fetchShippings();
  }, [fetchShippings]);

  // A narrowed list has fewer pages, so any filter change goes back to page 1.
  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedFilters(filters);
      setPage(1);
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters]);

  // Exports what the table shows: the applied filters, not inputs still being
  // typed, and every page of them.
  const exportCsv = async () => {
    try {
      setExporting(true);
      setExportError(null);
      const blob = await operatorConsole.exportShippings(filterParams(appliedFilters));
      saveBlob(blob, exportFilename(appliedFilters));
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setExporting(false);
    }
  };

  const totalPages = data ? Math.ceil(data.meta.total_count / data.meta.per) : 0;

  return (
    <div className="space-y-4 p-6">
      <div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Deliveries</h1>
            <p className="text-[12px] text-ui-text-secondary">
              Card deliveries your players ordered. Read-only — statuses, tracking and refunds are
              updated on our side; contact us if something looks wrong.
            </p>
            {data?.meta.sandbox && (
              <p className="mt-1 text-[12px] text-[var(--co-warning)]">
                Staging sandbox: open a test delivery to mark it shipped, delivered or canceled
                yourself — the real webhooks and callbacks follow.
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            onClick={() => void exportCsv()}
            disabled={exporting}
            className="shrink-0"
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        </div>
        {exportError && (
          <p className="mt-1 text-[12px] text-[var(--co-error)]">
            Export failed: {exportError}
          </p>
        )}
      </div>

      <ShippingsFilters values={filters} onChange={setFilters} />

      {loading ? (
        <div className="rounded-lg border border-ui-border bg-ui-card p-6 text-center text-ui-text-secondary">
          Loading deliveries...
        </div>
      ) : error ? (
        <div className="rounded-lg border border-ui-border bg-ui-card p-6 text-center text-[var(--co-error)]">
          Error: {error}
        </div>
      ) : !data || data.pool_shippings.length === 0 ? (
        <div className="rounded-lg border border-ui-border bg-ui-card p-6 text-center text-ui-text-secondary">
          No deliveries match these filters.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table aria-label="Deliveries" className="w-full border-collapse">
              <thead>
                <tr className="border-b border-ui-border">
                  {COLUMNS.map((column) => (
                    <th
                      key={column}
                      scope="col"
                      className="px-4 py-3 text-left text-sm font-semibold text-ui-text"
                    >
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.pool_shippings.map((shipping) => (
                  <ShippingRow key={shipping.id} shipping={shipping} />
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-ui-border bg-ui-card px-4 py-3">
            <div className="text-sm text-ui-text-secondary">
              Page {page} of {totalPages} • Total {data.meta.total_count} deliveries
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1 text-sm disabled:opacity-50"
              >
                Prev
              </Button>
              <Button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages}
                className="px-3 py-1 text-sm disabled:opacity-50"
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
